import { useEffect, useRef, useState } from 'react';
import { Button } from '@/design-system/Button';
import { TextInput } from '@/design-system/Inputs';
import { Dialog } from '@/design-system/Overlay';
import { startCameraDecoder, type Controls } from './cameraDecoder';

function cameraProblem(error: unknown): string {
  const name = error instanceof DOMException || error instanceof Error ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'Camera access was refused. Allow the camera for this site in your browser settings, or type the label value below.';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'No camera was found. Type the label value below.';
  return 'The camera could not be started. Type the label value below.';
}

/**
 * Reads one label value from the phone camera, with typing (or a keyboard-wedge scanner) as the
 * fallback. The camera stops whenever the dialog closes or a value is read, so the light does not
 * stay on behind the form.
 */
export function LabelScanner({ open, onClose, onScanned }: { open: boolean; onClose: () => void; onScanned: (value: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [typed, setTyped] = useState('');

  useEffect(() => {
    if (!open) return;
    setProblem(null);
    setTyped('');
    let controls: Controls | null = null;
    let stopped = false;
    const element = video.current;
    if (!element || !window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setProblem('The camera needs the secure (https) site and a browser that allows camera access. Type the label value below.');
      return;
    }
    startCameraDecoder(element, text => {
      if (stopped) return;
      stopped = true;
      controls?.stop();
      onScanned(text);
    }).then(started => {
      if (stopped) started.stop(); else controls = started;
    }).catch(error => { if (!stopped) setProblem(cameraProblem(error)); });
    return () => { stopped = true; controls?.stop(); };
  }, [open, onScanned]);

  return (
    <Dialog open={open} onClose={onClose} title="Scan a label" size="md"
      description="Point the camera at the asset’s QR code or barcode. Nothing is saved until you register the asset."
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={!typed.trim()} onClick={() => onScanned(typed)}>Use typed value</Button>
      </>}>
      <div className="space-y-4">
        <video ref={video} className="aspect-video w-full rounded bg-black" muted playsInline aria-label="Camera preview" hidden={problem !== null} />
        {problem && <p role="alert" className="text-small text-danger-text">{problem}</p>}
        <form onSubmit={event => { event.preventDefault(); if (typed.trim()) onScanned(typed); }}>
          <TextInput label="Or type the tag or serial" value={typed} onChange={event => setTyped(event.target.value)} autoComplete="off" />
        </form>
      </div>
    </Dialog>
  );
}
